import React, { useState, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Divider,
  Paper,
  IconButton,
  useMediaQuery,
  useTheme,
  Chip,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import SearchIcon from '@material-ui/icons/Search';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { useHistory } from 'react-router-dom';
import StoreContext from '../../Store/context';
import useLogin from '../../../utils/useLogin';
import { fetchCepInfo } from '../../../utils/brasilApi';
import { getHubApiUrl, getHubAuthHeaders } from '../../../utils/hubApi';
import { useFirebaseAuth } from '../../../contexts/FirebaseAuthContext';
import { auth, db } from '../../../firebase';
import { collection, onSnapshot, doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import axios from 'axios';

// Helper formatters
const formatCpfCnpj = (value) => {
  if (!value) return '';
  const clean = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  const hasLetters = /[A-Z]/.test(clean);

  if (hasLetters || clean.length > 11) {
    const c = clean.slice(0, 14);
    const p1 = c.slice(0, 2);
    const p2 = c.slice(2, 5);
    const p3 = c.slice(5, 8);
    const p4 = c.slice(8, 12);
    const p5 = c.slice(12, 14);

    let formatted = p1;
    if (p2) formatted += '.' + p2;
    if (p3) formatted += '.' + p3;
    if (p4) formatted += '/' + p4;
    if (p5) formatted += '-' + p5;
    return formatted;
  } else {
    const c = clean.slice(0, 11);
    const p1 = c.slice(0, 3);
    const p2 = c.slice(3, 6);
    const p3 = c.slice(6, 9);
    const p4 = c.slice(9, 11);

    let formatted = p1;
    if (p2) formatted += '.' + p2;
    if (p3) formatted += '.' + p3;
    if (p4) formatted += '-' + p4;
    return formatted;
  }
};

const formatCep = (value) => {
  if (!value) return '';
  const clean = value.replace(/\D/g, '').slice(0, 8);
  if (clean.length > 5) {
    return `${clean.slice(0, 5)}-${clean.slice(5)}`;
  }
  return clean;
};

const SubscriptionCheckoutModal = ({
  open,
  onClose,
  accountId = null,
  initialPlanId = '',
  initialAddonIds = null,
  initialData = null,
}) => {
  const theme = useTheme();
  const history = useHistory();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { credencials } = useContext(StoreContext);
  const { currentUser: activeUser } = useFirebaseAuth();

  const { loginWithToken } = useLogin({
    onCompleted: () => {
      if (onClose) onClose();
      history.replace('/home');
    },
  });

  const [step, setStep] = useState(1); // 1: Fiscal/Registration Data, 2: Plan & Addon Selection
  const [loading, setLoading] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);

  // Registration Form State
  const [companyName, setCompanyName] = useState('');
  const [pseudonym, setPseudonym] = useState('');
  const [responsibleName, setResponsibleName] = useState('');
  const [cpfCnpj, setCpfCnpj] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [address, setAddress] = useState('');
  const [addressNumber, setAddressNumber] = useState('');
  const [complement, setComplement] = useState('');
  const [province, setProvince] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');

  // Plans & Addons loaded from Firestore
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [addons, setAddons] = useState([]);
  const [selectedAddonIds, setSelectedAddonIds] = useState([]);

  const [paymentResult, setPaymentResult] = useState(null);
  const [copiedPix, setCopiedPix] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [hubAccountId, setHubAccountId] = useState(null);

  // Load plans from Firestore
  useEffect(() => {
    if (!open) return;
    const unsubPlans = onSnapshot(collection(db, 'plans'), (snap) => {
      let fetchedPlans = [];
      if (!snap.empty) {
        fetchedPlans = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(p => p.is_active !== false);
      }

      setPlans(fetchedPlans);
      if (!selectedPlanId && fetchedPlans.length > 0) {
        const highlight = fetchedPlans.find(p => p.is_highlight);
        setSelectedPlanId(initialPlanId || (highlight ? highlight.id : fetchedPlans[0].id));
      }
    });

    let addonsInitialized = false;
    const unsubAddons = onSnapshot(collection(db, 'addons'), (snap) => {
      if (!snap.empty) {
        const activeAddons = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(a => a.is_active !== false);
        setAddons(activeAddons);

        if (!addonsInitialized) {
          if (initialAddonIds && Array.isArray(initialAddonIds)) {
            setSelectedAddonIds(initialAddonIds);
          } else {
            const allAddonIds = activeAddons.map(a => a.id);
            setSelectedAddonIds(allAddonIds);
          }
          addonsInitialized = true;
        }
      }
    });

    return () => {
      unsubPlans();
      unsubAddons();
    };
  }, [open, initialPlanId, initialAddonIds]);

  // Initialize and load full company details on open
  useEffect(() => {
    if (!open) {
      setPaymentResult(null);
      setErrorMessage('');
      return;
    }

    if (accountId) {
      setHubAccountId(accountId);
    }

    if (initialPlanId) {
      setSelectedPlanId(initialPlanId);
    }

    if (initialAddonIds && Array.isArray(initialAddonIds)) {
      setSelectedAddonIds(initialAddonIds);
    }

    // 0. Pre-fill from initialData prop (e.g. from Onboarding)
    if (initialData) {
      if (initialData.companyName) setCompanyName(initialData.companyName);
      if (initialData.pseudonym) setPseudonym(initialData.pseudonym);
      if (initialData.responsibleName) setResponsibleName(initialData.responsibleName);
      if (initialData.cpfCnpj) setCpfCnpj(formatCpfCnpj(initialData.cpfCnpj));
      if (initialData.whatsapp) setWhatsapp(initialData.whatsapp);
      if (initialData.postalCode) setPostalCode(initialData.postalCode);
      if (initialData.address) setAddress(initialData.address);
      if (initialData.addressNumber) setAddressNumber(initialData.addressNumber);
      if (initialData.complement) setComplement(initialData.complement);
      if (initialData.province) setProvince(initialData.province);
      if (initialData.city) setCity(initialData.city);
      if (initialData.state) setState(initialData.state);
    }

    // 1. Pre-fill from session companyData
    if (credencials?.companyData) {
      const c = credencials.companyData;
      setCompanyName(prev => c.name || c.companyName || c.razao_social || prev);
      setPseudonym(prev => c.pseudonimo || c.pseudonym || c.name || prev);
      setResponsibleName(prev => c.responsible_name || c.responsibleName || c.name || prev);
      setCpfCnpj(prev => c.cnpj || c.cpf_cnpj || c.cpfCnpj || prev);
      setWhatsapp(prev => c.whatsapp || c.celular || c.telefone || prev);
      setPostalCode(prev => c.postal_code || c.postalCode || c.cep || prev);
      setAddress(prev => c.address || c.street || c.logradouro || prev);
      setAddressNumber(prev => c.address_number || c.addressNumber || c.numero || prev);
      setComplement(prev => c.complement || c.complemento || prev);
      setProvince(prev => c.province || c.bairro || prev);
      setCity(prev => c.city || c.cidade || prev);
      setState(prev => c.state || c.uf || prev);
    }

    // 2. Fetch full account record from Hub API & Firestore
    const loadFullAccountData = async () => {
      const apiUrl = getHubApiUrl();
      const targetAccountId = accountId || credencials?.companyData?.id || credencials?.company;
      let fetchedData = null;
      const headers = await getHubAuthHeaders(credencials, activeUser);

      if (targetAccountId) {
        try {
          const res = await axios.get(`${apiUrl}/accounts/${targetAccountId}`, { headers });
          if (res.data) fetchedData = res.data;
        } catch (e) {
          console.warn('Hub API get account warning:', e);
        }
      }

      if (!fetchedData && targetAccountId) {
        try {
          const docRef = doc(db, 'accounts', String(targetAccountId));
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) fetchedData = docSnap.data();
        } catch (e) {
          console.warn('Firestore account lookup warning:', e);
        }
      }

      if (fetchedData) {
        if (fetchedData.id) setHubAccountId(fetchedData.id);
        if (fetchedData.name) setCompanyName(fetchedData.name);
        if (fetchedData.pseudonym || fetchedData.name) setPseudonym(fetchedData.pseudonym || fetchedData.name);
        if (fetchedData.responsible_name || fetchedData.name) setResponsibleName(fetchedData.responsible_name || fetchedData.name);
        if (fetchedData.cpf_cnpj || fetchedData.cnpj) setCpfCnpj(fetchedData.cpf_cnpj || fetchedData.cnpj);
        if (fetchedData.whatsapp) setWhatsapp(fetchedData.whatsapp);
        if (fetchedData.postal_code || fetchedData.cep) setPostalCode(fetchedData.postal_code || fetchedData.cep);
        if (fetchedData.address) setAddress(fetchedData.address);
        if (fetchedData.address_number) setAddressNumber(fetchedData.address_number);
        if (fetchedData.complement) setComplement(fetchedData.complement);
        if (fetchedData.province) setProvince(fetchedData.province);
        if (fetchedData.city) setCity(fetchedData.city);
        if (fetchedData.state) setState(fetchedData.state);
      }
    };

    loadFullAccountData();
  }, [open, credencials, accountId, initialPlanId, initialAddonIds, initialData]);

  const handleFetchCep = async () => {
    if (!postalCode) return;
    setCepLoading(true);
    try {
      const data = await fetchCepInfo(postalCode);
      if (data) {
        if (data.street) setAddress(data.street);
        if (data.neighborhood) setProvince(data.neighborhood);
        if (data.city) setCity(data.city);
        if (data.state) setState(data.state);
      }
    } catch (e) {
      console.warn('CEP fetch warning:', e);
    } finally {
      setCepLoading(false);
    }
  };

  const handleToggleAddon = (addonId) => {
    setSelectedAddonIds(prev =>
      prev.includes(addonId) ? prev.filter(id => id !== addonId) : [...prev, addonId]
    );
  };

  const handleSaveRegistrationData = async () => {
    const finalCompanyName = companyName || pseudonym || 'Minha Empresa';
    const finalPseudonym = pseudonym || companyName || 'Minha Empresa';
    const finalResponsibleName = responsibleName || pseudonym || companyName || 'Responsável';

    setErrorMessage('');
    setLoading(true);

    try {
      const apiUrl = getHubApiUrl();
      const targetAccountId = hubAccountId || accountId || credencials?.companyData?.id || credencials?.company;

      const payload = {
        name: finalCompanyName,
        pseudonym: finalPseudonym,
        responsible_name: finalResponsibleName,
        cpf_cnpj: (cpfCnpj || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase(),
        whatsapp: (whatsapp || '').replace(/\D/g, ''),
        postal_code: (postalCode || '').replace(/\D/g, ''),
        address: address || '',
        address_number: addressNumber || '',
        complement: complement || '',
        province: province || '',
        city: city || '',
        state: state || '',
      };

      const headers = await getHubAuthHeaders(credencials, activeUser);

      if (targetAccountId) {
        try {
          await axios.put(`${apiUrl}/accounts/${targetAccountId}`, payload, { headers });
        } catch (e) {
          console.warn('Hub API update error:', e);
        }

        try {
          const docRef = doc(db, 'accounts', String(targetAccountId));
          await setDoc(docRef, {
            ...payload,
            updated_at: new Date(),
          }, { merge: true });
        } catch (e) {
          console.warn('Firestore update error:', e);
        }
      }

      setStep(2); // Proceed to plan selection
    } catch (e) {
      console.error('Error saving registration data:', e);
      let errorDetail = e.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        errorDetail = errorDetail.map(err => err.msg ? `${err.loc?.join('.')} ${err.msg}` : JSON.stringify(err)).join('; ');
      } else if (typeof errorDetail === 'object') {
        errorDetail = JSON.stringify(errorDetail);
      }
      setErrorMessage(errorDetail || 'Erro ao salvar dados cadastrais. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateSubscription = async () => {
    // Open blank window synchronously on user click to bypass browser popup blockers
    const invoiceWindow = window.open('about:blank', '_blank');

    setLoading(true);
    setErrorMessage('');

    try {
      const apiUrl = getHubApiUrl();
      const targetAccountId = hubAccountId || accountId || String(credencials?.companyData?.id || credencials?.company || '');

      const headers = await getHubAuthHeaders(credencials, activeUser);

      const params = {
        account_id: targetAccountId,
        email: activeUser?.email || credencials?.username || 'cliente@duzzsystem.com.br',
        plan_id: selectedPlanId,
        company_name: companyName,
        cpf_cnpj: (cpfCnpj || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase(),
        whatsapp: (whatsapp || '').replace(/\D/g, ''),
        responsible_name: responsibleName,
        postal_code: (postalCode || '').replace(/\D/g, ''),
        address,
        address_number: addressNumber,
        province,
        city,
        state,
        ...(selectedAddonIds.length > 0 ? { addon_ids: selectedAddonIds.join(',') } : {}),
      };

      const response = await axios.post(
        `${apiUrl}/subscriptions/create`,
        null,
        { params, headers }
      );

      const invoiceUrl = response.data?.invoiceUrl || response.data?.invoice_url || response.data?.payment_url;
      let loginToken = response.data?.login_token;
      if (!loginToken && response.data?.system_url) {
        try {
          const urlObj = new URL(response.data.system_url);
          loginToken = urlObj.searchParams.get('loginToken');
        } catch (e) {
          console.error('Error parsing system_url token:', e);
        }
      }

      if (invoiceUrl) {
        if (invoiceWindow && !invoiceWindow.closed) {
          invoiceWindow.location.href = invoiceUrl;
        } else {
          window.open(invoiceUrl, '_blank');
        }
      } else if (invoiceWindow && !invoiceWindow.closed) {
        invoiceWindow.close();
      }

      setPaymentResult({
        invoiceUrl: invoiceUrl || null,
        pixQrCode: response.data?.pix_qr_code,
        pixCopyPaste: response.data?.pix_copy_paste,
        loginToken: loginToken || null,
      });

      if (loginToken) {
        localStorage.removeItem('pendingBusinessData');
        loginWithToken(loginToken);
      }
    } catch (e) {
      if (invoiceWindow && !invoiceWindow.closed) {
        invoiceWindow.close();
      }
      console.error('Error creating subscription:', e);
      let errorDetail = e.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        errorDetail = errorDetail.map(err => err.msg ? `${err.loc?.join('.')} ${err.msg}` : JSON.stringify(err)).join('; ');
      } else if (typeof errorDetail === 'object') {
        errorDetail = JSON.stringify(errorDetail);
      }
      setErrorMessage(errorDetail || 'Erro ao gerar assinatura. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyPix = () => {
    if (paymentResult?.pixCopyPaste) {
      navigator.clipboard.writeText(paymentResult.pixCopyPaste);
      setCopiedPix(true);
      setTimeout(() => setCopiedPix(false), 3000);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: 20,
          padding: isDesktop ? 8 : 0,
        },
      }}
    >
      <DialogTitle style={{ paddingBottom: 0 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" style={{ fontWeight: 800, color: '#0f172a' }}>
            {step === 1 ? '1 de 2: Dados Cadastrais' : '2 de 2: Escolha seu Plano'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="caption" style={{ color: '#64748b' }}>
          {step === 1
            ? 'Confirme as informações fiscais e de contato da sua empresa'
            : 'Selecione o plano e módulos adicionais para ir para o pagamento'}
        </Typography>
      </DialogTitle>

      <DialogContent style={{ paddingTop: 20 }}>
        {errorMessage && (
          <Box mb={2.5} p={1.5} borderRadius={8} bgcolor="#fef2f2" border="1px solid #fca5a5">
            <Typography variant="body2" style={{ color: '#dc2626', fontWeight: 600 }}>
              {errorMessage}
            </Typography>
          </Box>
        )}

        {step === 1 ? (
          /* STEP 1: Fiscal / Registration Data */
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label="Razão Social da Empresa *"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label="Nome Fantasia / Pseudônimo *"
                value={pseudonym}
                onChange={(e) => setPseudonym(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label="Nome do Responsável *"
                value={responsibleName}
                onChange={(e) => setResponsibleName(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label="CPF / CNPJ *"
                value={cpfCnpj}
                onChange={(e) => setCpfCnpj(formatCpfCnpj(e.target.value))}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label="WhatsApp da Empresa *"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box display="flex" style={{ gap: 8 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  label="CEP *"
                  value={postalCode}
                  onChange={(e) => setPostalCode(formatCep(e.target.value))}
                  onBlur={handleFetchCep}
                />
                <IconButton color="primary" onClick={handleFetchCep} disabled={cepLoading} style={{ padding: 8 }}>
                  {cepLoading ? <CircularProgress size={20} /> : <SearchIcon />}
                </IconButton>
              </Box>
            </Grid>

            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label="Logradouro / Endereço *"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label="Número *"
                value={addressNumber}
                onChange={(e) => setAddressNumber(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label="Bairro *"
                value={province}
                onChange={(e) => setProvince(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label="Cidade *"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label="UF / Estado *"
                value={state}
                onChange={(e) => setState(e.target.value.toUpperCase())}
              />
            </Grid>
          </Grid>
        ) : (
          /* STEP 2: Plan & Addon Selection (from Firestore) & Asaas Checkout Redirect */
          <div>
            {!paymentResult ? (
              <div>
                {/* Add-ons Section */}
                {addons && addons.length > 0 && (
                  <Box mb={3} p={2} style={{ backgroundColor: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0' }}>
                    <Typography variant="subtitle2" style={{ fontWeight: 800, color: '#0f172a', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span>✨ Módulos Adicionais (Add-ons)</span>
                    </Typography>
                    <Grid container spacing={1}>
                      {addons.map(addon => {
                        const isChecked = selectedAddonIds.includes(addon.id);
                        return (
                          <Grid item xs={12} key={addon.id}>
                            <Paper
                              elevation={0}
                              onClick={() => handleToggleAddon(addon.id)}
                              style={{
                                padding: '8px 12px',
                                borderRadius: 8,
                                cursor: 'pointer',
                                border: `1px solid ${isChecked ? '#17497F' : '#cbd5e1'}`,
                                backgroundColor: isChecked ? '#eff6ff' : '#ffffff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                              }}
                            >
                              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  readOnly
                                  style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#17497F', margin: 0 }}
                                />
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                  <Typography variant="body2" style={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
                                    {addon.name}
                                  </Typography>
                                  {addon.description && (
                                    <Typography variant="caption" style={{ color: '#64748b', lineHeight: 1.2, marginTop: 2 }}>
                                      {addon.description}
                                    </Typography>
                                  )}
                                </div>
                              </Box>
                              <Typography variant="body2" style={{ fontWeight: 800, color: '#17497F', whiteSpace: 'nowrap', marginLeft: 8 }}>
                                + R$ {(addon.amount || 0).toFixed(2)}/mês
                              </Typography>
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                )}

                <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#334155', marginBottom: 12 }}>
                  Escolha o Plano de Licença:
                </Typography>

                <Grid container spacing={2}>
                  {plans.map((plan) => {
                    const isSelected = selectedPlanId === plan.id;
                    const planName = plan.reason || plan.name || 'Plano Licença';
                    const baseAmount = plan.amount || 0;

                    const getFrequencyMonths = (freq) => {
                      if (!freq) return 1;
                      const f = String(freq).toLowerCase().trim();
                      if (['yearly', 'year', 'years', 'annually', 'annual'].includes(f)) return 12;
                      if (['semiannually', 'semi-annual'].includes(f)) return 6;
                      if (['quarterly', 'quarter'].includes(f)) return 3;
                      if (['monthly', 'month', 'months'].includes(f)) return 1;
                      return 1;
                    };

                    const planMonths = getFrequencyMonths(plan.frequency);

                    const addonsTotal = selectedAddonIds.reduce((sum, addonId) => {
                      const addon = addons.find(a => a.id === addonId);
                      if (!addon) return sum;
                      const addonMonths = getFrequencyMonths(addon.frequency);
                      const multiplier = addonMonths > 0 ? planMonths / addonMonths : 1;
                      return sum + ((addon.amount || 0) * multiplier);
                    }, 0);

                    const totalPlanPrice = baseAmount + addonsTotal;
                    const frequency = plan.frequency === 'YEARLY' ? '/ano' : '/mês';

                    return (
                      <Grid item xs={12} sm={6} key={plan.id}>
                        <Paper
                          elevation={0}
                          onClick={() => setSelectedPlanId(plan.id)}
                          style={{
                            padding: 16,
                            borderRadius: 12,
                            cursor: 'pointer',
                            border: `2px solid ${isSelected ? '#17497F' : '#e2e8f0'}`,
                            backgroundColor: isSelected ? '#eff6ff' : '#ffffff',
                            position: 'relative',
                          }}
                        >
                          {plan.is_highlight && (
                            <Chip
                              label="RECOMENDADO"
                              size="small"
                              style={{
                                position: 'absolute',
                                top: -10,
                                right: 12,
                                backgroundColor: '#17497F',
                                color: '#fff',
                                fontWeight: 800,
                                fontSize: 10,
                              }}
                            />
                          )}
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle1" style={{ fontWeight: 800, color: '#0f172a' }}>
                              {planName}
                            </Typography>
                            {isSelected && <CheckCircleIcon style={{ color: '#17497F' }} />}
                          </Box>
                          <Typography variant="h6" style={{ fontWeight: 800, color: '#17497F', margin: '8px 0' }}>
                            R$ {totalPlanPrice.toFixed(2)}{' '}
                            <span style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>{frequency}</span>
                          </Typography>
                          {addonsTotal > 0 && (
                            <Typography variant="caption" style={{ color: '#2563eb', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                              (R$ {baseAmount.toFixed(2)} plano + R$ {addonsTotal.toFixed(2)} add-ons)
                            </Typography>
                          )}
                          <Typography variant="caption" style={{ color: '#64748b' }}>
                            {plan.description || 'Acesso completo ao Duzz Commercial.'}
                          </Typography>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </div>
            ) : (
              /* Asaas Checkout Redirect Result */
              <Box textAlign="center" py={2}>
                <CheckCircleIcon style={{ fontSize: 48, color: '#17497F', marginBottom: 8 }} />
                <Typography variant="h6" style={{ fontWeight: 800, color: '#0f172a' }}>
                  Checkout Gerado com Sucesso!
                </Typography>
                <Typography variant="body2" style={{ color: '#475569', marginBottom: 20 }}>
                  A página de pagamento foi aberta. Caso o navegador tenha bloqueado a aba, clique no botão abaixo:
                </Typography>

                {paymentResult.invoiceUrl && (
                  <Box mb={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      startIcon={<OpenInNewIcon />}
                      onClick={() => window.open(paymentResult.invoiceUrl, '_blank')}
                      style={{
                        backgroundColor: '#17497F',
                        color: '#fff',
                        fontWeight: 800,
                        borderRadius: 12,
                        padding: '12px 24px',
                      }}
                    >
                      Abrir Fatura
                    </Button>
                  </Box>
                )}

                {paymentResult.pixCopyPaste && (
                  <Box mb={2} style={{ maxWidth: 450, margin: '0 auto' }}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={paymentResult.pixCopyPaste}
                      inputProps={{ readOnly: true, style: { fontSize: 12, fontFamily: 'monospace' } }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      startIcon={<FileCopyIcon />}
                      onClick={handleCopyPix}
                      style={{ marginTop: 8, borderRadius: 10, fontWeight: 700 }}
                    >
                      {copiedPix ? 'Código Copiado!' : 'Copiar Código PIX Copia e Cola'}
                    </Button>
                  </Box>
                )}

                <Box mt={3} mb={1}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => {
                      if (paymentResult?.loginToken) {
                        localStorage.removeItem('pendingBusinessData');
                        loginWithToken(paymentResult.loginToken);
                      } else {
                        if (onClose) onClose();
                        history.replace('/home');
                      }
                    }}
                    style={{
                      backgroundColor: '#16a34a',
                      color: '#fff',
                      fontWeight: 800,
                      borderRadius: 12,
                      padding: '12px 28px',
                    }}
                  >
                    Acessar o Sistema Agora →
                  </Button>
                </Box>
              </Box>
            )}
          </div>
        )}
      </DialogContent>

      <DialogActions style={{ padding: 16, justifyContent: 'space-between' }}>
        {step === 2 && !paymentResult && (
          <Button onClick={() => setStep(1)} disabled={loading} style={{ fontWeight: 700 }}>
            Voltar
          </Button>
        )}

        {step === 1 ? (
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveRegistrationData}
            disabled={loading}
            style={{ borderRadius: 10, fontWeight: 800, padding: '10px 24px', backgroundColor: '#17497F' }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Ir para pagamento'}
          </Button>
        ) : (
          !paymentResult && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleGenerateSubscription}
              disabled={loading || !selectedPlanId}
              style={{ borderRadius: 10, fontWeight: 800, padding: '10px 24px', backgroundColor: '#17497F' }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Confirmar'}
            </Button>
          )
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SubscriptionCheckoutModal;
